import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material';
import Stack from '@mui/material/Stack';
import { PieChart, type PieChartProps, type PieValueType } from '@mui/x-charts';
import { useDrawingArea } from '@mui/x-charts/hooks';
import { StyledText } from '../../pages/Equipment/General/styles';

/** Reference size the gauge was designed against, every metric scales from it */
const BASE_WIDTH = 250;
const BASE_HEIGHT = 120;

const baseGaugeConfig: Partial<PieChartProps> = {
  margin: { top: 0, bottom: 0, left: 0, right: 0 },
};

const baseSeriesConfig = {
  innerRadius: 70,
  outerRadius: 100,
  cornerRadius: 5,
  startAngle: -90,
  endAngle: 90,
  cx: 125,
  cy: 110,
};

interface PieCenterLabelProps {
  children: React.ReactNode;
  scale: number;
}

/** Displays custom label on Pie Chart */
function PieCenterLabel({
  children,
  scale,
}: PieCenterLabelProps): React.ReactElement {
  const { width, height, left, top } = useDrawingArea();
  return (
    <StyledText
      x={left + width / 2}
      y={top + height / 2 + 20 * scale}
      style={{ fontSize: 20 * scale }}
    >
      {children}
    </StyledText>
  );
}

interface InternalChartProps {
  data?: PieValueType[];
  percentage: number | string;
  unit?: string;
  legendPosition?: 'top' | 'bottom' | 'middle' | undefined;
  tooltipFormatter?: (item: PieValueType) => string;
  maxWidth?: number;
}

function PieArcChart({
  data,
  percentage,
  legendPosition,
  unit = '%',
  tooltipFormatter,
  maxWidth = BASE_WIDTH,
}: InternalChartProps) {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(maxWidth);

  /** Keeps the gauge inside its container on narrow screens */
  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(([entry]) => {
      const available = entry.contentRect.width;
      if (available > 0) setWidth(Math.min(maxWidth, available));
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, [maxWidth]);

  const scale = width / BASE_WIDTH;

  return (
    <Stack
      ref={containerRef}
      direction="column"
      alignItems="center"
      sx={{
        width: '100%',
        maxWidth: `${maxWidth}px`,
        minWidth: 0,
        mx: 'auto',
      }}
    >
      {data && (
        <PieChart
          {...baseGaugeConfig}
          width={width}
          height={BASE_HEIGHT * scale}
          series={[
            {
              data: data,
              ...baseSeriesConfig,
              innerRadius: baseSeriesConfig.innerRadius * scale,
              outerRadius: baseSeriesConfig.outerRadius * scale,
              cornerRadius: baseSeriesConfig.cornerRadius * scale,
              cx: baseSeriesConfig.cx * scale,
              cy: baseSeriesConfig.cy * scale,
              valueFormatter:
                tooltipFormatter || ((item) => `${item.value}${unit}`),
            },
          ]}
          xAxis={[
            {
              valueFormatter: (value: string) => `${value}${unit}`,
            },
          ]}
          slotProps={{
            data: data,
            tooltip: {
              trigger: 'item',
              sx: {
                '& .MuiChartsTooltip-valueCell': { whiteSpace: 'pre-line' },
              },
            },
            legend: {
              // @ts-expect-error: library has a type error on current version but 'row' works well
              direction: 'row' as unknown,
              position: { vertical: legendPosition, horizontal: 'center' },
            },
          }}
        >
          <defs>
            <linearGradient
              id="pieChartGradient"
              x1="0%"
              y1="0%"
              x2="0%"
              y2="100%"
            >
              <stop offset="0%" stopColor={theme.palette.primary['04']} />
              <stop offset="100%" stopColor={theme.palette.primary['02']} />
            </linearGradient>
          </defs>

          <PieCenterLabel scale={scale}>{percentage}%</PieCenterLabel>
        </PieChart>
      )}
    </Stack>
  );
}

export default PieArcChart;
